import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { merge, Subject, takeUntil } from 'rxjs';
import { TranslocoDirective } from '@jsverse/transloco';
import { startWith } from 'rxjs/operators';

class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    return !!(
      control?.invalid &&
      (control.dirty || control.touched || form?.submitted)
    );
  }
}

@Component({
  selector: 'app-input-field',
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatTooltip,
    MatLabel,
    MatError,
    MatFormField,
    MatInput,
    TranslocoDirective,
    MatPrefix,
  ],
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.scss',
})
export class InputFieldComponent implements OnInit, OnDestroy {
  @Input({ required: true }) control: FormControl = new FormControl();
  @Input() label?: string;
  @Input() placeholder = '';
  @Input() helpText = '';
  @Input() type = 'text';
  @Input() icon?: string;
  @Input() bottomMargin = true;
  @Input() id = '';
  @Input() hideErrors = false;

  private readonly destroy$: Subject<void> = new Subject();

  errorMessage = signal('');
  readonly errorStateMatcher = new ImmediateErrorStateMatcher();

  ngOnInit() {
    merge(
      this.control.statusChanges
        .pipe(startWith(this.control.status))
        .pipe(takeUntil(this.destroy$)),
      this.control.valueChanges
        .pipe(startWith(this.control.value))
        .pipe(takeUntil(this.destroy$)),
    ).subscribe(this.updateErrorMessage.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateErrorMessage() {
    const { errors } = this.control;
    if (errors) {
      const [firstErrorKey] = Object.keys(errors);
      this.errorMessage.set(firstErrorKey || 'default');
    } else {
      this.errorMessage.set('');
    }
  }
}
